//External libs
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse} from '@angular/common/http';
import { Subscription } from 'rxjs';

//Local libs
import Service from 'src/app/common/service';
import BusinessCategoryModel from './businessCategory.model';

/**
 * @service BusinessCategoryService
 * @description Service for managing business category data operations.
 * Provides HTTP methods for retrieving business categories from the backend API.
 * Extends base Service class with generic type BusinessCategoryModel.
 *
 * @feature 1. Fetch all business categories with pagination and sorting
 * @feature 2. Cancel ongoing HTTP requests to prevent memory leaks
 * @feature 3. Promise-based async operations for business category management
 *
 * @architecture
 * Extends base Service<BusinessCategoryModel> providing REST API integration with configurable base URL
 * and service endpoint. Implements request tracking for cleanup and cancellation. Uses HttpClient
 * for HTTP operations with standard error handling.
 *
 * @service-endpoint /tradingcommunity/businesscategory/
 * @service-provider root (singleton, application-wide)
 *
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
@Injectable({
  providedIn: 'root'
})
export class BusinessCategoryService extends Service<BusinessCategoryModel> {

  /**
   * @type {Array<Subscription>}
   * @description Array tracking all ongoing HTTP request subscriptions
   * @default []
   * @side-effect Used to store subscription references for cancellation via cancelOnGoingHttpRequests()
   */
  public ongoingRequests: Subscription[] = [];

  /**
   * @method constructor
   * @description Service constructor with dependency injection of HttpClient
   *
   * Initializes service by calling parent Service constructor with baseUrl
   * configuration and service endpoint path.
   *
   * @param {HttpClient} httpClient - @service Angular HTTP client for making HTTP requests
   * @returns {void}
   */
  constructor(public httpClient: HttpClient) {
    super(httpClient, '/tradingcommunity/businesscategory/');
  }

  /**
   * @method getAllBusinessCategories
   * @async
   * @description Fetch all business category records from the backend
   *
   * Makes HTTP GET request to backend endpoint /tradingcommunity/businesscategory/
   * with query parameters for page size (2000) and order (new). Wraps response in Promise for
   * async/await compatibility.
   *
   * @returns {Promise<Array<BusinessCategoryModel>>} Promise resolving to array of BusinessCategoryModel objects
   * @side-effect
   * - Creates HTTP GET request and stores subscription reference in ongoingRequests
   * - Subscription can be cancelled via cancelOnGoingHttpRequests()
   *
   * @throws Rejects promise with HttpErrorResponse if request fails
   * @example
   * const categories = await businessCategoryService.getAllBusinessCategories();
   * console.log(categories); // Array of BusinessCategoryModel objects
   */
  public getAllBusinessCategories() {
    return new Promise<Array<BusinessCategoryModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<BusinessCategoryModel>>(`${this.baseUrl}${this.serviceUrl}?pageSize=2000&order=new`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  //To Cancel the onGoing Http Requests
  /**
   * @method cancelOnGoingHttpRequests
   * @description Cancel all ongoing/pending HTTP requests to prevent memory leaks
   *
   * Iterates through ongoingRequests array and unsubscribes from each active
   * subscription. Clears the requests array after unsubscribing from all.
   *
   * @execution-flow
   * 1. Iterate through each subscription in ongoingRequests array
   * 2. Check if subscription has unsubscribe function
   * 3. Call unsubscribe() on each active subscription
   * 4. Clear ongoingRequests array
   *
   * @returns {void}
   * @side-effect
   * - Aborts all pending HTTP requests
   * - Clears ongoingRequests array
   * - Prevents completion callbacks for cancelled requests
   *
   * @usage Called during component ngOnDestroy to cleanup subscriptions
   * @example
   * ngOnDestroy() {
   *   this.businessCategoryService.cancelOnGoingHttpRequests();
   * }
   */
  public cancelOnGoingHttpRequests(): void {
    this.ongoingRequests.forEach((request) => {
      if (request && typeof request.unsubscribe === 'function') {
        request.unsubscribe();
      }
    });
    this.ongoingRequests = [];
  }
}
